import DOMPurify from "dompurify";

const SORRY_TEXT = "<p>We're sorry, but we are experiencing a temporary service disruption. We expect this to be fixed soon. In the meantime, you can still get your questions answered by calling our licensed experts at (855) 734-5291.</p>";

const productIdMapping = {
  "Contractor's Equipment and Small Tools": "IM",
  "Equipment and Business Property": "IM",
  "General Liability": "GL",
  "Workers' Compensation": "WC",
  "Business Owner's Policy": "BOP",
  "Professional Liability / Errors & Omissions": "PL"
};

const isApprovedAnswer = (messageText) => {
  return !messageText.includes(SORRY_TEXT) && !messageText.includes("I'm going to need a little help from our licensed experts on this one");
}

function checkScrollPosition(animateContainer, chatContainer, downArrow) {
  if (!animateContainer || !chatContainer || !downArrow) return;

  const isScrolledToBottom =
    animateContainer.scrollTop + animateContainer.clientHeight >= chatContainer.scrollHeight - 25;

  if (isScrolledToBottom) {
    downArrow.style.display = "none";
  } else {
    downArrow.style.display = "block";
  }
}

function sendChatMessage(
  msg,
  chatWrapper,
  chatInput,
  promptOverride = undefined,
) {
  if (msg.trim().length === 0) {
    return;
  }
  const sendIcon = chatWrapper.querySelector(".reco-chat-send-icon");
  if (sendIcon) {
    sendIcon.classList.add("disabled-input");
  }

  let sessionId = chatWrapper.dataset.chatSessionId;
  const { insProduct } = chatInput.dataset;

  if (!sessionId) {
    sessionId = `${productIdMapping[insProduct]}::${crypto.randomUUID()}`;
    chatWrapper.dataset.chatSessionId = sessionId;
  }

  const msgsElem = chatWrapper.querySelector(".reco-chat-messages-inner");
  const loaderElem = chatWrapper.querySelector(".reco-chat-loader-wrapper");
  const isFirstMsg = !msgsElem.querySelector(".reco-chat-message");

  const promptsOuter = chatWrapper.querySelector(".reco-chat-prompts-outer");
  if (promptsOuter) {
    promptsOuter.style.display = "none";
  }

  chatInput.value = "";
  if (loaderElem) {
    loaderElem.style.display = "block";
  }
  chatInput.disabled = true;

  const userMsgHtml = DOMPurify.sanitize(
    `<div class="reco-chat-message chat-message-user">${msg}</div><div class="clearfix"></div>`,
  );
  msgsElem.insertAdjacentHTML("beforeend", userMsgHtml);

  const animateContainer = chatWrapper.closest(
    ".mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer",
  );
  const chatContainer = animateContainer?.querySelector(
    ".mobius-dialogue-modal__styled-drawer-content",
  );

  if (animateContainer && chatContainer) {
    animateContainer.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: "smooth",
    });
  }

  if (isFirstMsg && chatContainer) {
    const drawerContent = chatContainer.closest(".mobius-drawer__content");
    const drawerHeader = drawerContent?.previousElementSibling;
    if (drawerHeader?.classList.contains("mobius-drawer__header")) {
      drawerHeader.classList.add("minimized");
    }
  }

  const introElem = chatWrapper.querySelector(".reco-agent-intro");
  if (introElem) {
    const removedIntro = introElem.parentElement.removeChild(introElem);
    msgsElem.prepend(removedIntro);
  }

  const urlWithoutQueryParams = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("send_message", "true");
  urlParams.set("step_action", "recommendations_chat_api");
  urlParams.set("message", promptOverride || msg);
  urlParams.set("chat_id", chatWrapper.dataset.chatId);
  urlParams.set("authentication", chatWrapper.dataset.authentication);
  urlParams.set("last_quick_replies", chatWrapper.dataset.lastQuickReplies);
  if (productIdMapping[insProduct]) {
    urlParams.set("product", productIdMapping[insProduct].toLowerCase());
  }

  fetch(`${urlWithoutQueryParams}?${urlParams.toString()}`, {
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then((res) => {
      if (res.error) {
        throw new Error(res.error);
      }

      renderChatMessage(chatWrapper, res.messages);
      chatWrapper.dataset.lastQuickReplies = JSON.stringify(res.messages[1] || {});

      chatInput.disabled = false;
      const promptsElem = chatWrapper.querySelector(".reco-chat-prompts-outer");
      const innerPromptsElem = promptsElem?.querySelector(".reco-chat-prompts");

      if (innerPromptsElem?.querySelector("li")) {
        promptsElem.style.display = "";
      }

      if (loaderElem) {
        loaderElem.style.display = "none";
      }
      if (animateContainer && chatContainer) {
        const agentMsgs = chatContainer.querySelectorAll('.reco-chat-message.chat-message-agent');
        const mostRecentMsgElem = agentMsgs[agentMsgs.length - 1];
        const inputWrapper = chatWrapper.querySelector('.reco-chat-input-wrapper');

        animateContainer.scrollTo({
          top: (animateContainer.scrollHeight - mostRecentMsgElem.scrollHeight) - (promptsOuter.scrollHeight + inputWrapper.scrollHeight + 125),
          behavior: "smooth",
        });
        // Check if down arrow should be shown after scrolling
        const downArrow = chatWrapper.querySelector(".js-reco-chat-down-arrow");
        if (downArrow) {
          setTimeout(() => {
            checkScrollPosition(animateContainer, chatContainer, downArrow);
          }, 500); // Wait for scroll animation to complete
        }
      }
      if (chatInput.value.trim().length > 0 && sendIcon) {
        sendIcon.classList.remove("disabled-input");
      } else if (sendIcon) {
        sendIcon.classList.add("disabled-input");
      }
    })
    .catch((error) => {
      renderErrorMessage(chatWrapper);

      chatInput.disabled = false;
      if (loaderElem) {
        loaderElem.style.display = "none";
      }
      if (sendIcon) {
        sendIcon.classList.remove("disabled-input");
      }
    });
}

function renderChatMessage(chatWrapper, messages) {
  messages.forEach((item) => {
    if (item.type === "text") {
      const msgsElem = chatWrapper.querySelector(".reco-chat-messages-inner");
      let agentMsgHtml = DOMPurify.sanitize(
        `<div class="reco-chat-message chat-message-agent">${item.text}</div><div class="clearfix"></div>`,
      );

      const { startApplicationExperiment, insProduct } = chatWrapper.querySelector('textarea').dataset;

      if (startApplicationExperiment === "true" && isApprovedAnswer(item.text)) {
        const handoff = DOMPurify.sanitize(
          `<div class="reco-chat-message chat-message-agent"><p>Ready to get started? Start your <a id="products-application-handoff" href="#" style="text-decoration: underline;">${insProduct} application</a></p></div><div class="clearfix"></div><br />`,
        );
        agentMsgHtml += handoff;
      }

      msgsElem.insertAdjacentHTML("beforeend", agentMsgHtml);
    } else if (item.type === "radio_options") {
      const optionsHtml = DOMPurify.sanitize(
        `<ul>
          ${item.options
          .map((option) => {
            const sanitizedText = DOMPurify.sanitize(option.text);
            return `<li data-prompt="${sanitizedText}">${sanitizedText}</li><br />`;
          })
          .join('')}
        </ul>`,
        { ADD_TAGS: ['li', 'ul', 'br'], ADD_ATTR: [] }
      );
      const promptsContainer = chatWrapper.querySelector('.reco-chat-prompts.notranslate');

      clearQuickReplies(chatWrapper);

      promptsContainer.insertAdjacentHTML('afterbegin', optionsHtml);
    }
  });
}

function clearQuickReplies(chatWrapper) {
  if (!chatWrapper) return;

  const promptsContainer = chatWrapper.querySelector('.reco-chat-prompts.notranslate');
  if (promptsContainer) {
    while (promptsContainer.firstChild) {
      promptsContainer.removeChild(promptsContainer.firstChild);
    }
  }
}

function renderErrorMessage(chatWrapper) {
  if (!chatWrapper) return;

  // Clear quick replies first
  clearQuickReplies(chatWrapper);

  // Move intro to messages if it exists, so it appears before the error
  const introElem = chatWrapper.querySelector(".reco-agent-intro");
  const msgsElem = chatWrapper.querySelector(".reco-chat-messages-inner");
  if (introElem && msgsElem) {
    const removedIntro = introElem.parentElement.removeChild(introElem);
    msgsElem.prepend(removedIntro);
  }

  // Render the error message
  renderChatMessage(chatWrapper, [{ type: "text", text: SORRY_TEXT }]);
}

document.addEventListener("DOMContentLoaded", function () {
  if (!document.body.className.includes('usa'))
    return;

  // Disable all chat inputs by default
  document.querySelectorAll('.js-reco-chat-input').forEach(input => {
    input.disabled = true;
    const wrapper = input.closest('.js-reco-chat-wrapper');
    const sendIcon = wrapper?.querySelector('.reco-chat-send-icon');
    if (sendIcon) {
      sendIcon.classList.add('disabled-input');
    }
  });

  function setChatInputHeight(input) {
    input.style.height = "0";
    input.style.height = `${input.scrollHeight + 4}px`;
  }

  document.body.addEventListener("mouseenter", function (e) {
    if (e.target.matches(".reco-chat-beta-label")) {
      const popover = e.target.querySelector(".reco-chat-beta-popover");
      if (popover) {
        popover.style.display = "block";
      }
    }
  });

  document.body.addEventListener("mouseout", function (e) {
    if (e.target.matches(".reco-chat-beta-label")) {
      const popover = e.target.querySelector(".reco-chat-beta-popover");
      if (popover) {
        popover.style.display = "none";
      }
    }
  });

  document.body.addEventListener("click", function (e) {
    if (e.target.matches(".js-reco-chat-down-arrow")) {
      const chatWrapper = e.target.closest(".js-reco-chat-wrapper");
      const chatInput = chatWrapper?.querySelector(".js-reco-chat-input");
      const animateContainer = chatWrapper?.closest(
        ".mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer",
      );
      const chatContainer = animateContainer?.querySelector(
        ".mobius-dialogue-modal__styled-drawer-content",
      );
      const { insProduct } = chatInput.dataset;

      // Hide the down arrow when clicked
      e.target.style.display = "none";

      animateContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: "smooth" });

      const siteMeta = document.querySelector('meta[name="site"]');
      const siteContent = siteMeta ? siteMeta.getAttribute("content") : "";

      if (window.snowplowHelper?.trackFrontofficeStructEvent) {
        window.snowplowHelper.trackFrontofficeStructEvent({
          category: insProduct,
          action: "recommendations_ai_agent_down_arrow_clicked",
          serviceChannelIdentifier: siteContent,
        });
      }
    } else {

      const promptLi = e.target.closest(
        ".js-reco-chat-wrapper .reco-chat-prompts li",
      );
      if (promptLi) {
        const msg = promptLi.textContent;
        const promptOverride = promptLi.dataset.prompt;
        const wrapper = promptLi.closest(".js-reco-chat-wrapper");
        const input = wrapper?.querySelector(".js-reco-chat-input");

        if (wrapper && input) {
          sendChatMessage(msg, wrapper, input, promptOverride);
          setChatInputHeight(input);
        }

        const nextElement = promptLi.nextElementSibling;
        promptLi.remove();
        if (nextElement && nextElement.tagName === "BR") {
          nextElement.remove();
        }
      }

      const sendIcon = e.target.closest(
        ".js-reco-chat-wrapper .reco-chat-input-wrapper .reco-chat-send-icon",
      );
      if (sendIcon && !sendIcon.classList.contains("disabled-input")) {
        const wrapper = sendIcon.closest(".js-reco-chat-wrapper");
        const input = wrapper?.querySelector(".js-reco-chat-input");
        const msg = input?.value;

        if (wrapper && input && msg) {
          sendChatMessage(msg, wrapper, input);
          setChatInputHeight(input);
        }
      }

      const readMoreLink = e.target.closest(".reco-chat-read-more");
      if (readMoreLink) {
        const introParent = readMoreLink.closest(".reco-agent-intro");
        if (introParent) {
          introParent
            .querySelectorAll("span.reco-chat-intro-hidden")
            .forEach((el) => {
              el.style.display = "inline";
            });
          introParent
            .querySelectorAll(
              "p.reco-chat-intro-hidden, div.reco-chat-intro-hidden, h3.reco-chat-intro-hidden",
            )
            .forEach((el) => {
              el.style.display = "block";
            });

          introParent
            .querySelectorAll(".reco-chat-intro-after-read-hidden")
            .forEach((el) => {
              el.style.display = el.style.display === "none" ? "" : "none";
            });

          const chatWrapper = introParent.closest(".js-reco-chat-wrapper");
          const animateContainer = chatWrapper?.closest(
            ".mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer",
          );
          if (animateContainer) {
            animateContainer.scrollTo({ top: 0, behavior: "smooth" });
          }

          const drawerContent = chatWrapper?.closest(".mobius-drawer__content");
          const drawerHeader = drawerContent?.previousElementSibling;
          if (drawerHeader?.classList.contains("mobius-drawer__header")) {
            drawerHeader.classList.add("minimized");
          }

          const msgsElem = chatWrapper?.querySelector(
            ".reco-chat-messages-inner",
          );
          if (introParent && msgsElem && msgsElem.firstChild !== introParent) {
            const removedIntro =
              introParent.parentElement.removeChild(introParent);
            msgsElem.prepend(removedIntro);
          }

          const { insProduct } = readMoreLink.dataset;
          const siteMeta = document.querySelector('meta[name="site"]');
          const siteContent = siteMeta ? siteMeta.getAttribute("content") : "";
          if (window.snowplowHelper?.trackFrontofficeStructEvent) {
            window.snowplowHelper.trackFrontofficeStructEvent({
              category: insProduct,
              action: "recommendations_ai_agent_read_more_clicked",
              label: introParent.dataset.userMessage || "",
              serviceChannelIdentifier: siteContent,
            });
          }

          readMoreLink.remove();
        }
      }
    }

    const handoff = e.target.closest("#products-application-handoff");

    if (handoff) {
      const form = document.querySelector("form");

      // Uncheck all checkboxes in the form
      const checkboxes = form?.querySelectorAll('input[type="checkbox"]');
      checkboxes?.forEach(checkbox => {
        if (checkbox.checked) {
          checkbox.click();
        }
      });

      const chatWrapper = e.target.closest('.js-reco-chat-wrapper');
      const { insProduct } = chatWrapper.querySelector('textarea').dataset;

      const productInputName = () => {
        const productCode = productIdMapping[insProduct];
        if (productCode === "IM") {
          return "inland_marine_coverage";
        } else if (productCode === "GL") {
          return "general_liability_coverage";
        } else if (productCode === "WC") {
          return "workers_compensation_coverage";
        } else if (productCode === "BOP") {
          return "business_owners_policy_coverage";
        } else if (productCode === "PL") {
          return "professional_liability_coverage";
        } else {
          return "";
        }
      }

      const productCheckbox = form?.querySelector(`input#${productInputName()}_yes`);

      if (productCheckbox)
        productCheckbox.click();

      const siteMeta = document.querySelector('meta[name="site"]');
      const siteContent = siteMeta ? siteMeta.getAttribute("content") : "";
      if (window.snowplowHelper?.trackFrontofficeStructEvent) {
        window.snowplowHelper.trackFrontofficeStructEvent({
          category: chatWrapper.dataset.chatSessionId,
          action: "recommendations_ai_agent_journey_start_clicked",
          label: insProduct,
          value: chatWrapper.querySelectorAll(".reco-chat-messages-inner .chat-message-user").length,
          serviceChannelIdentifier: siteContent,
        });
      }

      form.submit();
    }

    const ctaButton = e.target.closest(".js-reco-agent-ai-cta button");
    if (ctaButton) {
      e.preventDefault();
      const modalContainer = ctaButton.closest(
        ".js-reco-agent-ai-cta",
      )?.parentElement;
      const modal = modalContainer?.querySelector(
        'div[data-react-app="Frontoffice.DialogueModal"]',
      );
      const openButton = modal?.querySelector("button");
      const chatInput = modal?.querySelector(".js-reco-chat-input");

      let insProduct;

      if (chatInput) {
        insProduct = chatInput.dataset.insProduct;
      }

      const chatWrapper = modal?.querySelector('.js-reco-chat-wrapper');

      if (openButton) openButton.click();

      if (
        typeof EventTracker !== "undefined" &&
        EventTracker.trigger &&
        insProduct &&
        !chatWrapper?.dataset.chatId
      ) {

        const drawerContent = chatWrapper?.closest('.mobius-drawer__content');
        const drawerHeader = drawerContent?.previousElementSibling.querySelector('.mobius-dialogue-modal__header');

        if (!drawerHeader.querySelector('.reco-chat-shield')) {
          const shieldDiv = document.createElement('div');
          shieldDiv.className = 'reco-chat-shield';
          drawerHeader.prepend(shieldDiv);
        }

        const siteMeta = document.querySelector('meta[name="site"]');
        const siteContent = siteMeta ? siteMeta.getAttribute("content") : "";

        let sessionId = chatWrapper.dataset.chatSessionId;
        const { insProduct } = chatInput.dataset;

        if (!sessionId) {
          sessionId = `${productIdMapping[insProduct]}::${crypto.randomUUID()}`;
          chatWrapper.dataset.chatSessionId = sessionId;
        }

        const urlWithoutQueryParams = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("create_chat", "true");
        urlParams.set("session_id", sessionId);
        urlParams.set("step_action", "recommendations_chat_api");
        urlParams.set("product_name", insProduct);
        urlParams.set("product_code", productIdMapping[insProduct].toLowerCase());

        fetch(`${urlWithoutQueryParams}?${urlParams.toString()}`, {
          headers: {
            "content-type": "application/json",
            accept: "application/json",
          },
        })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then((json) => {
            if (json.error) {
              throw new Error(json.error);
            }

            const chatWrapper = modal?.querySelector('.js-reco-chat-wrapper');
            chatWrapper.dataset.chatId = json.chat_id;
            chatWrapper.dataset.authentication = JSON.stringify(json.authentication);
            chatWrapper.dataset.lastQuickReplies = JSON.stringify(json.messages[0]);

            renderChatMessage(chatWrapper, json.messages);

            // Enable chat input now that we have chatId
            const chatInput = chatWrapper.querySelector('.js-reco-chat-input');
            if (chatInput) {
              chatInput.disabled = false;
              const sendIcon = chatWrapper.querySelector('.reco-chat-send-icon');
              if (sendIcon && chatInput.value.trim().length > 0) {
                sendIcon.classList.remove('disabled-input');
              }
            }
          })
          .catch((error) => {
            const chatWrapper = modal?.querySelector('.js-reco-chat-wrapper');
            renderErrorMessage(chatWrapper);
          });


        EventTracker.trigger(
          "recommendations_ai_agent_new_cta_clicked",
          insProduct,
          ctaButton.textContent.trim(),
        );
      }
    }

    const sparkles = e.target.closest(
      ".js-reco-agent-ai-cta span.reco-agent-ai-sparkles",
    );
    if (sparkles) {
      e.preventDefault();
      const modalContainer = sparkles.closest(
        ".js-reco-agent-ai-cta",
      )?.parentElement;
      const modal = modalContainer?.querySelector(
        'div[data-react-app="Frontoffice.DialogueModal"]',
      );
      const openButton = modal?.querySelector("button");
      const chatInput = modal?.querySelector(".js-reco-chat-input");
      const { insProduct } = chatInput.dataset;

      if (openButton) openButton.click();

      if (
        typeof EventTracker !== "undefined" &&
        EventTracker.trigger &&
        insProduct
      ) {
        EventTracker.trigger(
          "recommendations_ai_agent_new_cta_clicked",
          insProduct,
          "Sparkles",
        );
      }
    }
  });

  document.body.addEventListener("keypress", async function (e) {
    if (e.target.matches(".js-reco-chat-input") && e.key === "Enter") {
      e.preventDefault();
      const input = e.target;
      const msg = input.value;
      const wrapper = input.closest(".js-reco-chat-wrapper");

      if (wrapper) {
        sendChatMessage(msg, wrapper, input);
        setChatInputHeight(input);
      }
    }
  });

  document.body.addEventListener("input", function (e) {
    if (e.target.matches(".js-reco-chat-input")) {
      const input = e.target;
      const wrapper =
        input.closest(".reco-chat-input-wrapper") || input.parentElement;
      const sendIcon = wrapper?.querySelector(".reco-chat-send-icon");

      if (sendIcon && !input.disabled) {
        if (input.value.trim().length > 0) {
          sendIcon.classList.remove("disabled-input");
        } else {
          sendIcon.classList.add("disabled-input");
        }
      }
      setChatInputHeight(input);
    }
  });

  function setupScrollListeners(container) {
    const chatWrappers = container.querySelectorAll(".js-reco-chat-wrapper");

    chatWrappers.forEach((chatWrapper) => {
      const animateContainer = chatWrapper.closest(
        ".mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer",
      );
      const chatContainer = animateContainer?.querySelector(
        ".mobius-dialogue-modal__styled-drawer-content",
      );
      const downArrow = chatWrapper.querySelector(".js-reco-chat-down-arrow");

      if (animateContainer && chatContainer && downArrow && !animateContainer.hasAttribute("data-has-scroll-listener")) {
        animateContainer.setAttribute("data-has-scroll-listener", "true");

        animateContainer.addEventListener("scroll", () => {
          checkScrollPosition(animateContainer, chatContainer, downArrow);
        });

        // Initial check
        checkScrollPosition(animateContainer, chatContainer, downArrow);
      }
    });
  }

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Setup scroll listeners for any new chat wrappers
            setupScrollListeners(node);

            const dialogs = node.matches(
              'div[data-react-app="Frontoffice.DialogueModal"][data-react-props*="Coverage"] dialog',
            )
              ? [node]
              : node.querySelectorAll(
                'div[data-react-app="Frontoffice.DialogueModal"][data-react-props*="Coverage"] dialog',
              );

            dialogs.forEach((dialog) => {
              if (!dialog.hasAttribute("data-has-close-listener")) {
                dialog.setAttribute("data-has-close-listener", "true");

                dialog.addEventListener("close", () => {
                  if (
                    window.snowplowHelper &&
                    window.snowplowHelper.trackFrontofficeStructEvent
                  ) {
                    const siteMeta =
                      document.querySelector('meta[name="site"]');
                    const siteContent = siteMeta
                      ? siteMeta.getAttribute("content")
                      : "";
                    window.snowplowHelper.trackFrontofficeStructEvent({
                      category: window.location.href,
                      action: "recommendations_ai_agent_drawer_closed",
                      serviceChannelIdentifier: siteContent,
                    });
                  }
                });
              }
            });
          }
        });
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Setup scroll listeners for existing chat wrappers
  setupScrollListeners(document.body);
});
